import * as React from 'react';

import { sendDownloadInitiateMessage } from '~showbie/messages/sendDownloadMessage';

import { translate } from '~l10n/client';
import { Action } from '~shared/components/ActionMenu';
import { IconDownload } from '~shared/components/icons/standard';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

import { downloadEmptyWorkspaceQuiz } from '../../../../../api/quiz/downloadQuiz';

interface UseDownloadQuizActionParams {
  workspaceId: string;
  paywallCallback: () => void;
}

export function useDownloadQuizAction({
  paywallCallback,
  workspaceId,
}: UseDownloadQuizActionParams) {
  const {
    permissions: { downloadQuiz: canDownloadQuiz },
  } = useWorkspacePermissionsV2({ workspaceId });
  const appContext = useTeacherApplicationContext();

  const downloadQuizAction = React.useMemo(
    () =>
      ({
        key: 'download',
        title: translate('Download'),
        icon: <IconDownload />,
        action: ({ data, close }) => {
          const firstItem = data[0];
          if (firstItem.type === 'quiz') {
            const { id, workspaceId } = firstItem;

            downloadEmptyWorkspaceQuiz(
              id,
              workspaceId,
              appContext,
              (downloadUrl: string) => {
                // For Showbie Assessments, send Download.Initiate PostMessage
                if (
                  appContext === TeacherApplicationContext.ShowbieAssessments
                ) {
                  sendDownloadInitiateMessage(downloadUrl);
                }
              }
            );
            close();
          }
        },
        constraint: ({ data: quizzes }: { data: WorkspaceItem[] }) => {
          return quizzes.every((q) => q.type === 'quiz');
        },
        showProBadge: !canDownloadQuiz,
        disabled: ({ data: quizzes }) => quizzes.length > 1 || !canDownloadQuiz,
        paywallCallback: !canDownloadQuiz ? paywallCallback : null,
      } as Action<WorkspaceItem>),
    [canDownloadQuiz, paywallCallback, appContext]
  );

  return {
    downloadQuizAction,
  };
}
